import { useEffect, useMemo, useRef } from 'react';
import { usePathname } from 'expo-router';
import { useLocalRuntime, SimpleImageAttachmentAdapter, SimpleTextAttachmentAdapter, CompositeAttachmentAdapter } from '@assistant-ui/react';
import type { AttachmentAdapter, ThreadMessageLike } from '@assistant-ui/react';
import { getAPIBaseURL } from '../lib/api-url';
import { getPageContext } from '../lib/page-context';
import { AGENT_MODEL_AUTO } from '@tabbio/utils/agent-models';
import { AGENT_CHAT_FILE_ATTACHMENT_ACCEPT } from '@tabbio/utils/agent-chat-attachments';
import { readAgentModelPreference } from '../lib/agent-model-preference';
import { DEFAULT_AGENT_CAPABILITIES_PREFERENCE, readAgentCapabilitiesForRequest } from '../lib/agent-capabilities-preference';
import { readAgentMessageCapabilities } from '../lib/agent-capabilities-mentions';
import { derivePanelCommands } from '../lib/agent-panel-sync';
import { emitPanelCommands } from '../lib/panel-commands';
import { siteRuntimeParams } from '../lib/site-compose-runtime';
import { captureSentryException } from '../lib/sentry';
import { releaseSettledArtifactDrafts } from '../components/artifacts/artifact-draft-store';
import { draftOpenView, shouldAutoOpenDraft } from '../components/artifacts/artifact-draft-model';
import {
  ARTIFACT_PANEL_DOCK_QUERY,
  openArtifactPanelBuilding,
  readArtifactPanelLastClosedAt,
} from '../components/artifacts/artifact-panel-store';
import { createMastraChatModel } from './createMastraChatModel';
import { useInvalidateThreads } from './useThreads';
import { useChatPaywall } from './useChatPaywall';

const API_URL = getAPIBaseURL();

class SupportedFileAttachmentAdapter implements AttachmentAdapter {
  accept = AGENT_CHAT_FILE_ATTACHMENT_ACCEPT;

  async add({ file }: { file: File }) {
    return {
      id: Math.random().toString(36).slice(2),
      type: 'document' as const,
      name: file.name,
      contentType: file.type,
      file,
      status: {
        type: 'requires-action' as const,
        reason: 'composer-send' as const,
      },
    };
  }

  async send(attachment: any) {
    const file = attachment.file as File;
    try {
      if (typeof FileReader === 'undefined') {
        throw new Error('Attachment uploads are not available in this browser context.');
      }
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          if (typeof result !== 'string') {
            reject(new Error('Attachment read produced no data.'));
            return;
          }
          resolve(result.split(',')[1] ?? result);
        };
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read attachment'));
        reader.onabort = () => reject(reader.error ?? new Error('Attachment read was cancelled.'));
        reader.readAsDataURL(file);
      });

      return {
        ...attachment,
        status: { type: 'complete' as const },
        content: [
          {
            type: 'file' as const,
            data: base64,
            mimeType: file.type || 'application/octet-stream',
          },
        ],
      };
    } catch (error) {
      captureSentryException(error, {
        scope: 'attachment_adapter_send',
        runtime: 'mastra',
        fileName: file?.name,
        fileType: file?.type,
        fileSize: file?.size,
        hasFileReader: typeof FileReader !== 'undefined',
      });
      throw error;
    }
  }

  async remove() {}
}

const attachmentAdapter = new CompositeAttachmentAdapter([
  new SimpleImageAttachmentAdapter(),
  new SimpleTextAttachmentAdapter(),
  new SupportedFileAttachmentAdapter(),
]);

export function useMastraRuntime(
  mode: 'seeker' | 'employer' = 'seeker',
  threadId?: string,
  onThreadCreated?: (id: string) => void,
  initialMessages?: ThreadMessageLike[],
  companyId?: string,
) {
  const threadIdRef = useRef<string | undefined>(threadId);
  const invalidateThreads = useInvalidateThreads({ mode, companyId });

  // The sidebar's Recents list no longer polls, so the stream reporting a new
  // thread is what puts it there. The sidebar is always mounted on web with no
  // open event of its own, so this is its only same-session freshness trigger.
  const onThreadCreatedRef = useRef<((id: string) => void) | undefined>(undefined);
  const onThreadSettledRef = useRef<(() => void) | undefined>(undefined);

  // The chat column outlives route changes (three-pane shell), so the model
  // reads the LIVE pathname through a ref at send time (the same latch pattern
  // as threadIdRef) to attach the page context of the screen on display.
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  // Leaving a thread drops the writer drafts that are over: their cards read
  // the tool result from then on (plan 7.1).
  useEffect(() => () => releaseSettledArtifactDrafts(), [threadId]);

  useEffect(() => {
    onThreadCreatedRef.current = (id: string) => {
      onThreadCreated?.(id);
      invalidateThreads();
    };
  }, [onThreadCreated, invalidateThreads]);

  // The title and ordering timestamp are persisted after `start`; refreshing
  // at completion keeps the always-visible web sidebar in sync as well.
  // Out of credits (fair use): the paywall opens from the stream itself, at
  // once for a refused send and after the reply for a refused tool.
  const { onLiveToolResult: paywallOnToolResult, onReplySettled } = useChatPaywall(
    mode === 'employer' ? 'employer' : 'individual',
  );

  useEffect(() => {
    onThreadSettledRef.current = () => {
      invalidateThreads();
      onReplySettled();
    };
  }, [invalidateThreads, onReplySettled]);

  const onLiveToolResultRef = useRef(paywallOnToolResult);
  useEffect(() => {
    onLiveToolResultRef.current = paywallOnToolResult;
  }, [paywallOnToolResult]);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const chatModel = useMemo(
    () =>
      createMastraChatModel({
        apiUrl: API_URL,
        fetchImpl: (url, init) => fetch(url, init),
        mode,
        companyId,
        threadIdRef,
        onThreadCreatedRef,
        onThreadSettledRef,
        getPageContext: () => getPageContext(pathnameRef.current),
        // Read fresh from storage rather than closed over: the chat model is
        // memoized for the column's lifetime, so a captured value would pin
        // the model chosen when the column first mounted.
        getModelId: () => {
          const selected = readAgentModelPreference();
          return selected === AGENT_MODEL_AUTO ? undefined : selected;
        },
        // Match native: the "+" menu sets per-message chips, not sticky
        // device controls. A new message starts from Auto.
        getCapabilities: () => readAgentCapabilitiesForRequest(mode, DEFAULT_AGENT_CAPABILITIES_PREFERENCE),
        resolveMentions: (text, { hasAttachments }) =>
          readAgentMessageCapabilities(mode, text, {
            preference: DEFAULT_AGENT_CAPABILITIES_PREFERENCE,
            allowEmpty: hasAttachments,
          }),
        // Streamed tool results drive the content panel (Phase 3): CV writes
        // refresh the CV preview, application submits refresh My jobs, etc.
        onLiveToolResult: (activity) => {
          onLiveToolResultRef.current(activity);
          emitPanelCommands(derivePanelCommands(activity));
        },
        // Sites (plan 7.10): the page of a site-bound thread rides along as
        // artifactContext, and Sites mode turns its page into a draft site.
        ...siteRuntimeParams(),
        // The writer's first build of a reply docks the canvas panel on a wide
        // screen, on its code for a page (plan 7.2), unless the user closed
        // the panel during this reply or moved to another thread.
        onArtifactDraftStart: (event, { replyStartedAt, threadId: replyThreadId }) => {
          const wide =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia(ARTIFACT_PANEL_DOCK_QUERY).matches;
          const open = shouldAutoOpenDraft({
            wide,
            threadOnScreen: !replyThreadId || replyThreadId === threadIdRef.current,
            alreadyOpenedThisReply: false,
            replyStartedAt,
            lastUserCloseAt: readArtifactPanelLastClosedAt(),
          });
          if (open && event.artifactId) {
            openArtifactPanelBuilding(event.artifactId, draftOpenView(event.kind), { auto: true });
          }
        },
      }),
    [mode, companyId, threadIdRef, onThreadCreatedRef, onThreadSettledRef],
  );

  return useLocalRuntime(chatModel, {
    initialMessages: initialMessages ?? [],
    adapters: {
      attachments: attachmentAdapter,
    },
  });
}
